// Het tabblad Onderwerpen van Configuratie (W6 ronde 6; research §3.7, besluit V6).
//
// Twee delen:
//  1. De vaste onderwerpenlijst zoals de server hem telt: per onderwerp hoeveel
//     getoonde trends en vaardigheden het letterlijk noemen, per sector; per
//     sector hoeveel er (nog) geen onderwerp heeft; en wat in de data staat maar
//     niet (meer) in de lijst. De lijst zelf is een oordeel van de owner — dit
//     scherm wijzigt hem niet (de reden staat onder Instellingen, "Niet instelbaar").
//  2. Voorstellen uit de betekenis (V6): de server groepeert de trends zonder
//     onderwerp op hun embedding. Een dry-run: geen taalmodel, geen naam, en
//     geen knop om een groep live te zetten — een nieuw onderwerp komt pas in de
//     lijst na owner-go. Duurt op de echte data ~5 s, dus alleen op een klik.
//
// Het scherm rekent niets: "2.578 van 3.563" zijn twee getallen van de server
// naast elkaar, geen aandeel. De overeenkomst en de samenhang staan er zoals ze
// binnenkomen (0,94), niet afgerond en niet als percentage.
//
// De versie van de lijst ("topics-v1-draft") staat bewust niet op het scherm:
// het is een interne naam, en een beheerder leest er niets in.

import { Fragment, useEffect, useId, useRef, useState } from "react";

import { Card } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import { api } from "../../lib/api";
import type { ConfiguratieResponse, TopicRegel, TopicvoorstellenResponse } from "../../contracts";
import { color, sectionLabel, size, weight } from "../../tokens";
import { aantal, Blok, cijfer, getal, Kpi, noot } from "./BronnenStukken";
import { GroepIcoon } from "./Iconen";
import { cel, kopCel, Tabel } from "./KostenStukken";
import { focusKwijt } from "./ConfiguratieInstellingen";
import { invoer, knopDonker } from "./stijl";
import { leesbaar } from "./woorden";

type Topics = ConfiguratieResponse["topics"];
type Sector = Topics["zonderOnderwerp"][number];
type Groep = TopicvoorstellenResponse["groepen"][number];

export function Onderwerpen({ topics }: { topics: Topics }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <Blok titel="De onderwerpenlijst">
        <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55, marginBottom: 12 }}>
          {topics.uitleg}
        </p>
        <Lijst lijst={topics.lijst} />
      </Blok>
      <ZonderOnderwerp sectoren={topics.zonderOnderwerp} />
      <NietInLijst rijen={topics.nietInLijst} />
      <Voorstellen sectoren={topics.zonderOnderwerp} />
    </div>
  );
}

/** De onderwerpen per soort, in de volgorde van de server (die van de lijst). */
function perSoort(lijst: readonly TopicRegel[]): [string, TopicRegel[]][] {
  const groepen = new Map<string, TopicRegel[]>();
  for (const t of lijst) {
    const g = groepen.get(t.soort);
    if (g) g.push(t);
    else groepen.set(t.soort, [t]);
  }
  return [...groepen];
}

/** "regelgeving" → "Regelgeving": de soort is al een Nederlands woord uit de lijst. */
function soortTitel(soort: string): string {
  return soort ? soort.charAt(0).toUpperCase() + soort.slice(1) : "Zonder soort";
}

function Lijst({ lijst }: { lijst: readonly TopicRegel[] }) {
  if (lijst.length === 0) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason="de server stuurde geen onderwerpen" />
      </Card>
    );
  }
  return (
    <Tabel label="Onderwerpen met hun trends en vaardigheden" minBreedte={680}>
      <thead>
        <tr>
          <th scope="col" style={kopCel()}>
            Onderwerp
          </th>
          <th scope="col" style={kopCel(true)}>
            Trends
          </th>
          <th scope="col" style={kopCel(true)}>
            Vaardigheden
          </th>
          <th scope="col" style={kopCel()}>
            Per sector
          </th>
        </tr>
      </thead>
      {perSoort(lijst).map(([soort, rijen]) => (
        <tbody key={soort} data-soort={soort}>
          <tr>
            <th colSpan={4} scope="colgroup" style={groepKop}>
              {soortTitel(soort)}
            </th>
          </tr>
          {rijen.map((t) => (
            <OnderwerpRij key={t.naam} t={t} />
          ))}
        </tbody>
      ))}
    </Tabel>
  );
}

function OnderwerpRij({ t }: { t: TopicRegel }) {
  return (
    <tr data-onderwerp={t.naam}>
      <td style={cel()}>
        <div style={{ color: color.ink, fontWeight: weight.medium }}>{t.naam}</div>
      </td>
      <td style={cel(true)}>
        <Telling n={t.trends} />
      </td>
      <td style={cel(true)}>
        <Telling n={t.skills} />
      </td>
      <td style={cel()}>
        {t.reden ? (
          <EmptyReason reason={t.reden} />
        ) : t.perSector.length === 0 ? (
          <EmptyReason reason="de server gaf geen verdeling per sector" />
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {t.perSector.map((s) => (
              <li key={s.sector}>
                {`${s.label}: ${aantal(s.trends, "trend", "trends")} · ${aantal(
                  s.skills,
                  "vaardigheid",
                  "vaardigheden",
                )}`}
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}

/** Een telling van de server; nul staat er gewoon, maar lichter. */
function Telling({ n }: { n: number }) {
  return <span style={{ color: n === 0 ? color.faint : color.ink }}>{getal(n)}</span>;
}

function ZonderOnderwerp({ sectoren }: { sectoren: readonly Sector[] }) {
  return (
    <Blok titel="Zonder onderwerp">
      {sectoren.length === 0 ? (
        <Card style={{ padding: 18 }}>
          <EmptyReason reason="de server telde geen sector met trends of vaardigheden" />
        </Card>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          {/* Groot getal in een kaart (owner-ontwerptaal): twee getallen van de
              server naast elkaar, "x van y" — geen aandeel dat het scherm uitrekent. */}
          {sectoren.map((s) => (
            <Fragment key={s.sector}>
              <Kpi
                label={`${s.label} · trends`}
                waarde={`${getal(s.trendsZonder)} van ${getal(s.trendsTotaal)}`}
                onder="trends zonder onderwerp"
              />
              <Kpi
                label={`${s.label} · vaardigheden`}
                waarde={`${getal(s.skillsZonder)} van ${getal(s.skillsTotaal)}`}
                onder="vaardigheden zonder onderwerp"
              />
            </Fragment>
          ))}
        </div>
      )}
    </Blok>
  );
}

function NietInLijst({ rijen }: { rijen: Topics["nietInLijst"] }) {
  return (
    <Blok titel="In de data, niet in de lijst">
      <Card style={{ padding: "14px 18px" }}>
        {rijen.length === 0 ? (
          <EmptyReason reason="geen — elk onderwerp in de data staat ook in de lijst" />
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {rijen.map((r, i) => (
              <li
                key={r.naam}
                data-niet-in-lijst={r.naam}
                style={{
                  padding: "8px 0",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  fontSize: size.meta,
                }}
              >
                <span style={{ color: color.ink, fontWeight: weight.medium }}>{r.naam}</span>
                <span style={{ color: color.soft }}>
                  {` · ${aantal(r.trends, "trend", "trends")} · ${aantal(
                    r.skills,
                    "vaardigheid",
                    "vaardigheden",
                  )}`}
                </span>
                <div style={{ fontSize: 12, color: color.soft, marginTop: 3, lineHeight: 1.5 }}>
                  {r.reden}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Blok>
  );
}

// ── Voorstellen uit de betekenis (V6) ────────────────────────────────────────

// Een uitkomst en een fout onthouden voor welke sector ze gevraagd zijn (niet
// de sector die de server terugzegt: dat is zijn woord, niet onze vraag).
type VoorstelStaat =
  | { soort: "rust" }
  | { soort: "bezig" }
  // `focus`: stond de focus bij het binnenkomen nog op de knop (of was hij
  // kwijt)? Alleen dan gaat hij naar de uitkomst.
  | { soort: "klaar"; sector: string; data: TopicvoorstellenResponse; focus: boolean }
  | { soort: "fout"; sector: string; reden: string };

function Voorstellen({ sectoren }: { sectoren: readonly Sector[] }) {
  const [keuze, setKeuze] = useState(sectoren[0]?.sector ?? "");
  // De gekozen sector zolang de server hem noemt, anders de eerste. Dit vlak
  // blijft staan terwijl de configuratie opnieuw binnenkomt (na een opslag):
  // had de eerste ronde geen sectoren en brengt een latere ze wel, dan staat
  // de eerste klaar — niet een lege keuze waarop de knop niets doet.
  const sector = sectoren.some((s) => s.sector === keuze) ? keuze : (sectoren[0]?.sector ?? "");
  const [staat, setStaat] = useState<VoorstelStaat>({ soort: "rust" });
  const id = useId();
  const knop = useRef<HTMLButtonElement>(null);
  const bezig = staat.soort === "bezig";

  // Een antwoord hoort bij de sector waarvoor het gevraagd is: anders lees je
  // de groepen van de ene sector onder de naam van de andere. Een andere
  // sector kiezen zet de staat terug; deze toets vangt het ene geval zonder
  // keuze: een nieuwe ronde van de configuratie die de gekozen sector niet
  // meer noemt (dan springt `sector` hierboven naar de eerste).
  const uitkomst = staat.soort === "klaar" && staat.sector === sector ? staat : null;
  const fout = staat.soort === "fout" && staat.sector === sector ? staat.reden : null;

  async function maak() {
    // De knop is tijdens het groeperen aria-disabled, niet uit: hij houdt de
    // focus, en een tweede klik stopt hier.
    if (bezig || !sector) return;
    const gevraagd = sector;
    setStaat({ soort: "bezig" });
    try {
      const data = await api.topicvoorstellen(gevraagd);
      // Wie intussen naar een ander tabblad of veld ging, houdt zijn plek.
      const focus = focusKwijt() || document.activeElement === knop.current;
      setStaat({ soort: "klaar", sector: gevraagd, data, focus });
    } catch (err) {
      setStaat({
        soort: "fout",
        sector: gevraagd,
        reden: leesbaar(err, "De voorstellen konden niet worden gemaakt — probeer het zo nog eens."),
      });
      // De foutzin meldt zichzelf (role="alert"); de focus hoort op de knop
      // om het opnieuw te proberen. Staat hij daar al, dan blijft hij.
      if (focusKwijt()) knop.current?.focus();
    }
  }

  return (
    <Blok titel="Voorstellen uit de betekenis">
      <Card style={{ padding: "16px 18px" }}>
        {sectoren.length === 0 ? (
          <EmptyReason reason="de server telde geen sector om voorstellen voor te maken" />
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
              <div style={veld}>
                <label htmlFor={`${id}-sector`} style={veldLabel}>
                  Sector
                </label>
                {/* Een keuzelijst, geen schuif (owner-ontwerptaal). Tijdens het
                    groeperen dicht: het antwoord hoort bij de gekozen sector. */}
                <select
                  id={`${id}-sector`}
                  value={sector}
                  disabled={bezig}
                  onChange={(e) => {
                    setKeuze(e.target.value);
                    setStaat({ soort: "rust" });
                  }}
                  style={{ ...invoer, width: "auto", minWidth: 200 }}
                >
                  {sectoren.map((s) => (
                    <option key={s.sector} value={s.sector}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                ref={knop}
                type="button"
                onClick={() => void maak()}
                aria-disabled={bezig}
                style={{ ...knopDonker(bezig), display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <GroepIcoon /> {bezig ? "bezig… (± 5 s)" : "Maak voorstellen"}
              </button>
            </div>

            {/* Vóór het antwoord zegt het scherm wat de knop doet en waarom er
                geen knop is om een groep te benoemen of live te zetten (V6).
                Daarna zegt de uitleg van de server het, en staat dit er niet
                nog eens naast. */}
            {!uitkomst && (
              <p style={noot}>
                De server groepeert de trends zonder onderwerp van deze sector op betekenis. Dat
                duurt een paar seconden, daarom pas als je op Maak voorstellen klikt. Een groep kun
                je hier niet benoemen of live zetten: een nieuw onderwerp komt pas in de lijst na
                owner-go.
              </p>
            )}

            {/* Een levend gebied dat er altijd staat: een schermlezer meldt dan
                zowel "bezig" als "klaar" (zelfde afspraak als de raming op
                Kosten & credits). De groepen zelf staan eronder, niet hierin —
                twintig groepen voorlezen helpt niemand. */}
            <div role="status" aria-live="polite" data-voorstellen-status>
              {bezig && (
                <p style={{ ...noot, color: color.body }}>
                  De voorstellen worden gemaakt — dat duurt een paar seconden…
                </p>
              )}
              {uitkomst && (
                <p style={{ ...noot, color: color.body }}>
                  {`De voorstellen voor ${uitkomst.data.sectorLabel} staan hieronder.`}
                </p>
              )}
            </div>
            {fout && (
              <p role="alert" style={{ marginTop: 10, fontSize: size.meta, color: color.accentRed }}>
                {fout}
              </p>
            )}
          </>
        )}
      </Card>
      {uitkomst && <VoorstellenUitkomst data={uitkomst.data} focus={uitkomst.focus} />}
    </Blok>
  );
}

function VoorstellenUitkomst({ data: d, focus }: { data: TopicvoorstellenResponse; focus: boolean }) {
  const kop = useRef<HTMLHeadingElement>(null);

  // Wie op de knop klikte, gaat verder bij de uitkomst — niet zelf de pagina
  // afzoeken. Wie intussen elders stond (een ander tabblad), niet: dan zou de
  // focus in een verborgen tabblad belanden.
  useEffect(() => {
    if (focus) kop.current?.focus();
  }, [d, focus]);

  return (
    <div data-voorstellen style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
      <h3 ref={kop} tabIndex={-1} style={{ ...sectionLabel, marginBottom: 0, outline: "none" }}>
        {`Voorstellen · ${d.sectorLabel}`}
      </h3>
      <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55 }}>{d.uitleg}</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
        }}
      >
        {/* Twee getallen zoals de server ze stuurt. Het eerste is niet de
            "zonder onderwerp"-kaart hierboven: het telt de vaktrends zonder
            passend onderwerp (geen tag en Jev past onder de lat), dezelfde
            groep als de volledigheidspoort en de levensloop, met een
            embedding — de groep die gegroepeerd wordt (reparatie 06-10). Zijn ze
            gelijk, dan is niets afgekapt; is er wél afgekapt, dan zegt de
            server dat in `grens`. */}
        <Kpi
          label="Vaktrends zonder passend onderwerp"
          waarde={getal(d.trendsZonderOnderwerp)}
          onder="dezelfde telling als de poort, te groeperen"
        />
        <Kpi label="Meegenomen in de groepen" waarde={getal(d.meegenomen)} />
      </div>
      {d.grens && (
        <p role="note" style={{ fontSize: size.meta, color: color.accentAmber, lineHeight: 1.5 }}>
          {d.grens}
        </p>
      )}
      {d.groepen.length === 0 ? (
        <Card style={{ padding: 18 }}>
          <EmptyReason reason={d.reden ?? "de server maakte geen groepen"} />
        </Card>
      ) : (
        <>
          {d.reden && <p style={{ ...noot, marginTop: 0 }}>{d.reden}</p>}
          <ol
            aria-label="Voorgestelde groepen"
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))",
              gap: 12,
            }}
          >
            {d.groepen.map((g) => (
              <GroepKaart key={g.nr} g={g} />
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

/** Eén groep zoals de server hem stuurt. Bewust alleen lezen: geen veld voor
 *  een naam en geen knop om hem live te zetten (V6). */
function GroepKaart({ g }: { g: Groep }) {
  return (
    <li data-groep={g.nr}>
      <Card style={{ padding: "14px 16px", height: "100%", boxSizing: "border-box" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink }}>
            {`Groep ${g.nr}`}
          </span>
          <span style={{ fontSize: size.meta, color: color.body, ...cijfer }}>
            {aantal(g.grootte, "trend", "trends")}
          </span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: color.soft,
            marginTop: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            ...cijfer,
          }}
        >
          <span>{`samenhang ${getal(g.samenhang)}`}</span>
          <span>
            {g.lijktOp ? (
              `lijkt op: ${g.lijktOp.naam} (overeenkomst ${getal(g.lijktOp.overeenkomst)})`
            ) : (
              <EmptyReason reason="geen bestaand onderwerp om mee te vergelijken" />
            )}
          </span>
        </div>
        {g.titels.length === 0 ? (
          <div style={{ marginTop: 10 }}>
            <EmptyReason reason="de server stuurde geen titels bij deze groep" />
          </div>
        ) : (
          <ul
            style={{
              margin: "10px 0 0",
              paddingLeft: 18,
              fontSize: size.meta,
              color: color.body,
              lineHeight: 1.45,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {g.titels.map((t, i) => (
              // Twee trends kunnen dezelfde titel dragen: de plek is de sleutel.
              <li key={i}>{t}</li>
            ))}
          </ul>
        )}
      </Card>
    </li>
  );
}

const groepKop = {
  ...cel(),
  fontWeight: weight.medium,
  color: color.ink,
  background: color.surfaceQuote,
  textAlign: "left",
} as const;

const veld = { display: "flex", flexDirection: "column", gap: 4 } as const;
const veldLabel = { fontSize: 12, fontWeight: weight.medium, color: color.soft } as const;
